/**
 * Live tab counts for the add-players drawer's Watchlists / My Roster tabs
 * (SE-6947, PC1).
 *
 * PC1's acceptance criteria put a live count on all three tabs. The Suggested
 * count was already derivable (the panel reads the suggestions itself); the
 * two pool tabs could not state one truthfully before SE-6945 because their
 * queries selected no `totalCount` — the only number available was "rows
 * fetched so far". `KanbanPlayerConnection` now selects the connection's
 * `totalCount`, so the panel can read the real pool totals.
 *
 * ## Variables mirror the tab bodies EXACTLY
 *
 * These are the SAME documents the tab bodies run (`usePlayerQuery`), with
 * the same variables — page size 40, the full watchlist id set, the GPV
 * bounds when the board's cost filter is active. Identical variables are what
 * let Apollo deduplicate the panel's read against the mounted tab's and
 * collapse both onto one normalized cache entry (the SE-5058 fragmentation
 * lesson), so opening a tab never refetches what the count already loaded.
 *
 * The counts are POOL totals — before the board's client-side filters and
 * with placed players still counted, which is also what each tab renders
 * (placed players show as markers, not gaps).
 *
 * ## Confirmed-empty vs. not-yet-known (SE-7096)
 *
 * My Roster's count has no precondition — it is read unconditionally once the
 * drawer is open, so it always eventually resolves to a real number (0 or
 * otherwise). Watchlists' pool query additionally `skip`s while
 * `watchlistIds` is empty, because there is nothing to pool over — but an
 * empty array means the SAME thing whether the id list hasn't loaded yet
 * (real total unknown), it errored out (also unknown — a network failure is
 * not a confirmed zero), or it loaded successfully and the org/user
 * genuinely has zero watchlists (real total is a known 0). All three left
 * `watchlist` at `undefined` — a permanently bare tab label with no number,
 * ever, for any user/org with no watchlists, while My Roster kept working.
 * `isWatchlistIdsLoadedSuccessfully` (from `useWatchlistFilterOptions`)
 * disambiguates them: only report `0` once the id list read has actually
 * SUCCEEDED and is empty — not merely once it has stopped loading.
 * @module features/player-kanban/hooks/useAddPlayersTabCounts
 */

import {
  useKanbanListMyOrganizationsPlayersQuery,
  useKanbanListWatchlistPlayersQuery,
} from "@/generated/graphql";
import { useTeamSelection } from "@/providers/TeamSelectionProvider";

import type { ServerGpvRange } from "../utils/playerFilter";

/** Page size shared with `usePlayerQuery` — MUST stay equal for cache reuse. */
const PAGE_SIZE = 40;

/** The two pool counts, absent while unknown. */
export interface AddPlayersTabCounts {
  /** Total players across the org's watchlists, or `undefined` while unknown. */
  readonly watchlist?: number;
  /** Total org-roster players, or `undefined` while unknown. */
  readonly roster?: number;
}

/**
 * Read the Watchlists / My Roster pool totals for the tab strip.
 * @param isOpen - Whether the drawer is open. A closed drawer fires no reads.
 * @param watchlistIds - Every watchlist id, resolved by the panel.
 * @param serverGpvRange - Active GPV bounds, mirrored from the tab body.
 * @param isWatchlistIdsLoadedSuccessfully - Whether `watchlistIds` itself
 * finished resolving WITHOUT error. Distinguishes "empty because not loaded
 * yet or errored" (count stays unknown) from "empty because it loaded
 * successfully and there are none" (count is a known 0) — see the module
 * doc's SE-7096 note.
 * @returns The two counts, each absent until its total is known.
 */
const useAddPlayersTabCounts = (
  isOpen: boolean,
  watchlistIds: readonly string[],
  serverGpvRange: ServerGpvRange | null,
  isWatchlistIdsLoadedSuccessfully: boolean
): AddPlayersTabCounts => {
  const { isLoaded: isTeamSelectionLoaded } = useTeamSelection();

  const { data: rosterData } = useKanbanListMyOrganizationsPlayersQuery({
    variables: { first: PAGE_SIZE },
    skip: !isOpen || !isTeamSelectionLoaded,
    notifyOnNetworkStatusChange: true,
  });

  const { data: watchlistData } = useKanbanListWatchlistPlayersQuery({
    variables: {
      watchlistIds: [...watchlistIds],
      first: PAGE_SIZE,
      ...(serverGpvRange
        ? {
            minGeminiPlayerValuation: serverGpvRange.min,
            maxGeminiPlayerValuation: serverGpvRange.max,
          }
        : {}),
    },
    // SE-7404: this query shares ONE normalized cache bucket with the
    // Watchlists tab body (`usePlayerQuery`) — same document, same variables,
    // deliberately (see module doc). `keyArgs` in `ApolloClient.tsx` excludes
    // `first`/`after`, so any write here with no `after` is a BASE-PAGE write.
    // Apollo's `relayStylePagination` merge treats a base-page write as a
    // REPLACEMENT of the cached edges (see its `if (incoming.edges) prefix =
    // []` branch), not an append. `cache-and-network` re-issued that
    // cursor-less base-page fetch on every remount of this hook (e.g. every
    // drawer re-open), silently resetting the list back to page 1 even after
    // the user had scrolled it further — the tab's `totalCount` badge stayed
    // correct (unaffected by the reset) while the rendered rows regressed, a
    // count/rows split of the kind PROJECT_RULES.md's "Feed category duality"
    // note already warns about. `cache-first` still fetches on an EMPTY cache
    // (first mount, identical to before) but never again once the bucket is
    // populated, so it can no longer clobber `fetchMore`'s accumulated pages.
    skip: !isOpen || watchlistIds.length === 0,
    fetchPolicy: "cache-first",
  });

  return {
    // SE-7096: an empty id list that has SUCCESSFULLY finished loading (not
    // merely stopped loading — an error also stops it) is a known total of
    // 0, since there is nothing to pool over. Anything else (still in
    // flight, or errored) leaves the total genuinely unknown.
    watchlist:
      watchlistIds.length === 0
        ? isWatchlistIdsLoadedSuccessfully
          ? 0
          : undefined
        : (watchlistData?.listWatchlistPlayers?.totalCount ?? undefined),
    roster: rosterData?.listMyOrganizationsPlayers?.totalCount ?? undefined,
  };
};

export default useAddPlayersTabCounts;
